// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The resource view record as data (spec 2026-10-13-resource-view-record):
 * which image a selection shows, what a "Brug som ressourcebillede" choice
 * writes, and the URL/size to fetch. There is deliberately no fallback to a
 * ranked "best frame": a part without a view shows "Intet billede valgt".
 */

import { api } from '../api/client';
import type { ResourceView, SurveyPart, SurveyType, ViewRegion } from '../api/types';
import { resolveHighlightPart } from './photo';
import type { ResourceIndex } from './resources';

export const VIEW_KEY = 'sys:view';

export const VIEW_TEXT = {
  none: 'Intet billede valgt',
  hint: 'Vælg et foto under Fotos eller en 360°-optagelse med »Brug som ressourcebillede«.',
  use: 'Brug som ressourcebillede',
  chosen: 'Valgt',
} as const;

/** The panorama window a choice stores: ~60° wide, 45° tall (spec §4). */
export const PANO_WINDOW_DEG = { width: 60, height: 45 } as const;

/**
 * A frame choice: `region` is always `null` here — the server fills in the
 * instance's padded bbox for that frame on PATCH (`core::complete_view_region`,
 * spec §B/ruling 2026-10-13). The frontend never computes a frame region
 * itself; doing so would mean loading the instance's cloud just to pick one.
 */
export function frameView(frameId: number): ResourceView {
  return { kind: 'frame', id: frameId, region: null };
}

/**
 * A panorama choice: the window centred on the part's equirect `u, v`,
 * wrapped so `x` is in [0, 1) (it may cross the seam) and clamped vertically.
 * A levelled panorama's `u` rests on a guessed heading, so it stores the
 * whole image instead of a window that may point at the wrong wall.
 */
export function panoView(panoramaId: number, u: number, v: number, resected: boolean): ResourceView {
  if (!resected) return { kind: 'panorama', id: panoramaId, region: null };
  const w = PANO_WINDOW_DEG.width / 360;
  const h = PANO_WINDOW_DEG.height / 180;
  const x = (((u - w / 2) % 1) + 1) % 1;
  const y = Math.min(Math.max(v - h / 2, 0), 1 - h);
  return { kind: 'panorama', id: panoramaId, region: [x, y, w, h] };
}

/**
 * The `sys:view` PATCH value: compact JSON in the server's key order. A
 * resource always has an image once one is chosen — the server refuses a
 * `null` write once `sys:view` is set, so there is no write that clears one;
 * only a different `ResourceView` ever gets written here.
 */
export function viewValue(view: ResourceView): string {
  return JSON.stringify({ kind: view.kind, id: view.id, region: view.region });
}

export function isViewOf(view: ResourceView | null, kind: ResourceView['kind'], id: number): boolean {
  return view !== null && view.kind === kind && view.id === id;
}

/** The table cell for a stored `sys:view` value: `Foto #12` / `360° #4`; '' when unset or unreadable. */
export function viewCellText(value: string | null): string {
  if (value === null) return '';
  try {
    const v = JSON.parse(value) as Partial<ResourceView>;
    if (v.kind === 'frame') return `Foto #${v.id}`;
    if (v.kind === 'panorama') return `360° #${v.id}`;
  } catch {
    // fall through
  }
  return '';
}

export function viewCaption(view: ResourceView): string {
  return view.kind === 'frame' ? `Ressourcebillede · ramme ${view.id}` : `Ressourcebillede · 360° ${view.id}`;
}

/**
 * What a selection shows (`shown`) and where a choice made on it goes
 * (`target`, with its current view `targetView`). A part: its own. A type:
 * shows its first part (in part order) with a view; a choice goes to the
 * part whose frames the photo strip lists — the type's first linked part
 * (`resolveHighlightPart`, shared with the evidence panel).
 */
export function selectionView(
  type: SurveyType | null,
  part: SurveyPart | null,
  index: ResourceIndex,
): { target: string | null; targetView: ResourceView | null; shown: ResourceView | null } {
  if (part) {
    const own = index.get(part.code)?.view ?? null;
    return { target: part.code, targetView: own, shown: own };
  }
  const linked = resolveHighlightPart(type, null);
  return {
    target: linked?.code ?? null,
    targetView: linked ? (index.get(linked.code)?.view ?? null) : null,
    shown: rowThumbView(type?.parts ?? [], index),
  };
}

/** A row thumbnail's view: a part's own; a type's first part that has one. `null` = placeholder. */
export function rowThumbView(parts: readonly Pick<SurveyPart, 'code'>[], index: ResourceIndex): ResourceView | null {
  for (const p of parts) {
    const v = index.get(p.code)?.view;
    if (v) return v;
  }
  return null;
}

/** Edge length to fetch so the region (not the whole image) fills `displaySize` px, at most `cap`. */
export function viewFetchSize(view: ResourceView, displaySize: number, cap = 2048): number {
  const span = view.region ? Math.max(view.region[2], view.region[3]) : 1;
  return Math.min(cap, Math.max(displaySize, Math.ceil(displaySize / span)));
}

export function viewImageUrl(view: ResourceView, maxSize: number): string {
  return view.kind === 'frame'
    ? api.frameImageUrl(view.id, 'color', { maxSize })
    : api.panoramaImageUrl(view.id, { maxSize });
}

export interface CropStyle {
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
}

/**
 * Background geometry that shows `region` of an image (aspect = width /
 * height) centred in `box`. `contain` (the default) scales so the region
 * just fits, whole (the image around it fills the rest — or, past the
 * image's edge, the box shows through). `cover` never shows the box: the
 * image is scaled up just enough to cover it and slid back inside its
 * edges, so the region stays as whole and as central as that allows — for
 * an image drawn edge to edge. A panorama (`wrap`) repeats horizontally, so
 * a region across the seam shows both sides.
 */
export function cropBackground(
  region: ViewRegion | null,
  imageAspect: number,
  box: { width: number; height: number },
  wrap: boolean,
  fit: 'contain' | 'cover' = 'contain',
): CropStyle {
  const repeat = wrap ? 'repeat-x' : 'no-repeat';
  if (!(imageAspect > 0) || !(box.width > 0) || !(box.height > 0)) {
    return { backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: repeat };
  }
  const [x, y, w, h] = region ?? [0, 0, 1, 1];
  let imgH = Math.min(box.width / (w * imageAspect), box.height / h);
  if (fit === 'cover') imgH = Math.max(imgH, box.width / imageAspect, box.height);
  const imgW = imgH * imageAspect;
  let px = box.width / 2 - (x + w / 2) * imgW;
  let py = box.height / 2 - (y + h / 2) * imgH;
  if (fit === 'cover') {
    py = Math.min(0, Math.max(box.height - imgH, py));
    if (!wrap) px = Math.min(0, Math.max(box.width - imgW, px));
  }
  if (wrap) px = ((px % imgW) + imgW) % imgW;
  return { backgroundSize: `${imgW}px ${imgH}px`, backgroundPosition: `${px}px ${py}px`, backgroundRepeat: repeat };
}

/** A Fotos-strip thumbnail: whether it is the chosen view, and its accessible label. */
export function photoThumbState(view: ResourceView | null, frameId: number): { chosen: boolean; label: string } {
  const chosen = isViewOf(view, 'frame', frameId);
  return { chosen, label: chosen ? `Ramme ${frameId} · ${VIEW_TEXT.chosen}` : `${VIEW_TEXT.use}: ramme ${frameId}` };
}

/**
 * Whether the Foto tab's hint (pointing at the Fotos strip) should show: only
 * while choosing is actually possible — `canWrite` (`onChooseView` is
 * present) *and* `hasTarget` (the selection resolves to a linked part —
 * `selectionView(...).target !== null`) — and there is no image shown yet. A
 * manual/unlinked part, or a type with no linked part at all, has
 * `hasTarget: false` — its Fotos strip says "ikke koblet til en instans" and
 * there is nothing a choice could write to, so the hint would read as an
 * instruction the user cannot follow. A read-only viewer already sees "Intet
 * billede valgt" in the image well itself (`evidenceSources`' `empty` text),
 * so showing the hint too would also read as an instruction they cannot act
 * on. Once a resource has an image, there is nothing left for this row to
 * show — only a different photo/360° thumbnail can change it.
 */
export function fotoViewHint(shown: ResourceView | null, canWrite: boolean, hasTarget: boolean): boolean {
  return !shown && canWrite && hasTarget;
}

/**
 * The one image a Bygning inspector shows for a type (frontend-r22.1): the
 * focused part's own view when a part is focused, else the type's first part
 * (in part order) that has one — with the part it belongs to, so a click
 * opens that part's evidence. `view: null` = "Intet billede valgt"; there is
 * no ranked best-frame fallback.
 */
export function shownView(
  type: Pick<SurveyType, 'parts'>,
  partCode: string | null,
  index: ResourceIndex,
): { code: string | null; view: ResourceView | null } {
  if (partCode !== null && type.parts.some((p) => p.code === partCode)) {
    return { code: partCode, view: index.get(partCode)?.view ?? null };
  }
  for (const p of type.parts) {
    const v = index.get(p.code)?.view;
    if (v) return { code: p.code, view: v };
  }
  return { code: null, view: null };
}

/** A 360° choice's button: »Brug som ressourcebillede«, or »Valgt ✓« when this panorama is the view. */
export function panoChoiceState(view: ResourceView | null, panoramaId: number): { chosen: boolean; text: string } {
  const chosen = isViewOf(view, 'panorama', panoramaId);
  return { chosen, text: chosen ? `${VIEW_TEXT.chosen} ✓` : VIEW_TEXT.use };
}
